import { FC, useEffect, useMemo, useState } from "react";
import { Stack, useTheme } from "@mui/material";
import {
  FormikSelectField,
  FormikCheckbox,
  FormikToggleField,
  TableToggleField
} from "@vilocnv/allsetra-core";
import { FormLabelText, LabelRowBox } from "./InnerForm.styled";
import TimeFilters from "../../TimeFilters/TimeFilters";
import DaysFilters from "../../DaysFilter/DaysFilter";
import { isEmpty } from "lodash";
import { mapKeysWithDisplayLabel } from "app/data/helpers";

interface Props {
  groups: Array<any>;
  objectTypes: Array<any>;
  objects: Array<any>;
  keys: Array<any>;
  dataLoading?: boolean;
  translator: any;
  fetchOnDebounce: (value: string) => void;
  onChange?: (val?: any) => void;
  values: any;
  setFieldValue: any;
}

const InnerForm: FC<Props> = ({
  groups,
  objectTypes,
  objects,
  keys,
  dataLoading = false,
  translator,
  fetchOnDebounce: _fetchOnDebounce,
  onChange,
  values,
  setFieldValue
}) => {
  const theme = useTheme();
  const [isSelectDaysAndTimeEnable, setIsSelectDaysAndTimeEnable] =
    useState(false);
  const [prevAssignedToAllObjects, setPrevAssignedToAllObjects] = useState(
    values?.assignedToAllObjects
  );

  const keyOptions = useMemo(() => mapKeysWithDisplayLabel(keys), [keys]);

  const handleToggleDaysAndTimeChange = (value: boolean) => {
    setIsSelectDaysAndTimeEnable(value);
  };

  useEffect(() => {
    if (values?.assignedToAllObjects === prevAssignedToAllObjects) {
      return;
    }

    if (values.assignedToAllObjects) {
      setFieldValue("object", []);
      onChange?.([]);
    }

    setPrevAssignedToAllObjects(values.assignedToAllObjects);
  }, [values.assignedToAllObjects]);

  const commonFieldProps = {
    fullWidth: true
  };

  return (
    <Stack spacing={2}>
      <FormikCheckbox
        theme={theme}
        name="assignedToAllObjects"
        label={translator("selectAllObjects.label", {
          ns: "formFieldsTranslation"
        })}
      />

      {!values.assignedToAllObjects && (
        <>
          <LabelRowBox>
            <FormLabelText>
              {translator("objects.label", { ns: "formFieldsTranslation" })}
            </FormLabelText>
          </LabelRowBox>
          <FormikSelectField
            theme={theme}
            placeholder={translator("objects.placeholder", {
              ns: "formFieldsTranslation"
            })}
            name="object"
            options={objects ?? []}
            {...commonFieldProps}
            optionLabelKey="name"
            optionValueKey="uniqueId"
            loading={dataLoading}
            multiple
            searchable
            onChange={onChange}
            sx={{
              marginTop: "0 !important",
              "& .MuiAutocomplete-hasClearIcon.css-1x6bjyf-MuiAutocomplete-root .MuiOutlinedInput-root":
                {
                  paddingRight: "9px"
                }
            }}
            //@ts-ignore
            showStates
            showDropdownIcon
          />
        </>
      )}

      <FormikSelectField
        theme={theme}
        label={translator("objectType.label", { ns: "formFieldsTranslation" })}
        placeholder={translator("objectType.placeholder", {
          ns: "formFieldsTranslation"
        })}
        name="objectType"
        searchable
        {...commonFieldProps}
        options={objectTypes ?? []}
        optionLabelKey="name"
        optionValueKey="uniqueId"
        loading={dataLoading}
        multiple
      />
      {groups?.length ? (
        <FormikSelectField
          theme={theme}
          label={translator("groups.label", { ns: "formFieldsTranslation" })}
          placeholder={translator("groups.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="group"
          {...commonFieldProps}
          options={groups ?? []}
          optionLabelKey="name"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
        />
      ) : (
        <></>
      )}

      {!isEmpty(keys) && (
        <FormikSelectField
          theme={theme}
          {...commonFieldProps}
          label={translator("keys.label", { ns: "formFieldsTranslation" })}
          placeholder={translator("keys.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="key"
          options={keyOptions}
          optionLabelKey="displayLabel"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
        />
      )}

      <TableToggleField
        label={translator("common.selectDaysAndTime") ?? "Select days and time"}
        name="selectDaysAndTime"
        onChange={(e) => handleToggleDaysAndTimeChange(e.target.checked)}
        checked={isSelectDaysAndTimeEnable}
      />

      {isSelectDaysAndTimeEnable && (
        <>
          <DaysFilters values={values} />

          <TimeFilters values={values} />
        </>
      )}

      <FormikToggleField
        name="showInactiveObjects"
        label={
          translator("common.showInactiveObjects") || "Show inactive objects"
        }
      />
    </Stack>
  );
};

export default InnerForm;
